import { apiClient, bearer, unwrap } from './client';
import type { operations } from './schema';
import type {
  ApprovalReason,
  FindingListOut,
  KbAnswerOut,
  FindingOut,
  FindingRecoveryEventListOut,
  FindingStatusEventListOut,
  ReasonCode,
  RunFindingsFacetsOut,
  RunFindingsSummaryOut,
  SuggestionOut,
  SupplierBnMapOut,
  SupplierGraphOut,
  SupplierJournalEntriesOut,
  SupplierViewOut,
} from './types';

/** Typed query surface of `GET /api/runs/{run_id}/findings` — status +
 *  attached + the INP-778 pushdown filters (expense_source /
 *  ledger_source / outcome / category, all repeatable), `order`, and
 *  limit/offset paging. */
export type FindingsListQuery = NonNullable<
  operations['list_run_findings']['parameters']['query']
>;

/** One page of findings, filters pushed down to SQL. `total` is the
 *  filter-matched count independent of the page; `next_offset` is set
 *  while more rows remain. The server caps `limit` at 1000. */
export function listFindingsPageForRun(
  runId: string,
  idToken: string,
  query: FindingsListQuery,
): Promise<FindingListOut> {
  return apiClient
    .GET('/api/runs/{run_id}/findings', {
      params: { path: { run_id: runId }, query },
      headers: bearer(idToken),
    })
    .then(unwrap<FindingListOut>);
}

/** Server-aggregated per-status counts + recovery totals for the run —
 *  the review queue's summary chips read this instead of counting
 *  loaded rows, so the numbers stay honest under pagination (INP-778). */
export function getRunFindingsSummary(
  runId: string,
  idToken: string,
): Promise<RunFindingsSummaryOut> {
  return apiClient
    .GET('/api/runs/{run_id}/findings/summary', {
      params: { path: { run_id: runId } },
      headers: bearer(idToken),
    })
    .then(unwrap<RunFindingsSummaryOut>);
}

/** Distinct filter-dropdown values for a run's reviewable findings,
 *  computed server-side (one grouped query per facet) so the filter bar
 *  never has to load every finding to enumerate its options. Mirrors
 *  `getRunFindingsSummary` — a small, findings-load-independent query. */
export function getRunFindingsFacets(
  runId: string,
  idToken: string,
): Promise<RunFindingsFacetsOut> {
  return apiClient
    .GET('/api/runs/{run_id}/findings/facets', {
      params: { path: { run_id: runId } },
      headers: bearer(idToken),
    })
    .then(unwrap<RunFindingsFacetsOut>);
}

/** Page size + fan-out width for the full-run load below. 1000 is the
 *  server-side `limit` cap; 4 concurrent page fetches keeps a 54k-row
 *  run to ~14 request waves without stampeding the API. */
const FULL_LOAD_PAGE_SIZE = 1000;
const FULL_LOAD_CONCURRENCY = 4;

/** Load EVERY finding on the run by walking offset pages to the server
 *  total — replaces the old 4×5,000 status-scoped fan-out, which
 *  silently truncated any status bucket past its cap (INP-778).
 *
 *  Pages use the default `emission` order, which is immutable for a
 *  persisted run (rule id/version + transaction id never change), so
 *  concurrent dispositions can't shift rows between offset pages the
 *  way a status-filtered walk would. Pages after the first are fetched
 *  with bounded concurrency; rows are de-duped on finding_id as a
 *  defense-in-depth merge. */
export async function listFindingsForRun(
  runId: string,
  idToken: string,
  opts: { reviewableOnly?: boolean } = {},
): Promise<FindingListOut> {
  // `reviewableOnly` skips the auto-cleared no_issue coverage rows (the
  // bulk of an engagement-scale run). The run page loads the reviewable
  // set by default and only pulls coverage when the practitioner opts in
  // (the audit tab / "show auto-cleared"), so the common load is ~4-5x
  // smaller — the coverage rows are hidden from the queue/list anyway.
  const reviewable_only = opts.reviewableOnly ?? false;
  // Only the first page needs the `total` (to know how many pages to
  // walk). Recomputing it on every page is the dominant server cost at
  // engagement scale — a scan over the run's rows per request — so the
  // rest pass `with_total=false`.
  const first = await listFindingsPageForRun(runId, idToken, {
    limit: FULL_LOAD_PAGE_SIZE,
    offset: 0,
    reviewable_only,
    with_total: true,
  });
  const total = first.total ?? first.items.length;
  const byId = new Map<string, FindingOut>(first.items.map((i) => [i.finding_id, i]));
  const offsets: number[] = [];
  for (let o = FULL_LOAD_PAGE_SIZE; o < total; o += FULL_LOAD_PAGE_SIZE) offsets.push(o);
  let next = 0;
  const worker = async () => {
    while (next < offsets.length) {
      const offset = offsets[next++];
      const page = await listFindingsPageForRun(runId, idToken, {
        limit: FULL_LOAD_PAGE_SIZE,
        offset,
        reviewable_only,
        with_total: false,
      });
      for (const item of page.items) byId.set(item.finding_id, item);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(FULL_LOAD_CONCURRENCY, offsets.length) }, worker),
  );
  return { items: [...byId.values()], next_offset: null, total };
}

/** Fetch one finding by id. The detail page uses this instead of pulling
 *  the whole run's finding list just to `.find()` one row — a direct
 *  navigation (cold cache) then loads a single small payload rather than
 *  up to 5000 findings, so the card + invoice + journal start rendering
 *  immediately. Throws `ApiError(404)` when the id doesn't exist. */
export function getFinding(findingId: string, idToken: string): Promise<FindingOut> {
  return apiClient
    .GET('/api/findings/{finding_id}', {
      params: { path: { finding_id: findingId } },
      headers: bearer(idToken),
    })
    .then(unwrap<FindingOut>);
}

export function approveFinding(
  findingId: string,
  idToken: string,
  reason: ApprovalReason,
  note?: string,
): Promise<FindingOut> {
  return apiClient
    .POST('/api/findings/{finding_id}/approve', {
      params: { path: { finding_id: findingId } },
      body: { reason, ...(note !== undefined ? { note } : {}) },
      headers: bearer(idToken),
    })
    .then(unwrap<FindingOut>);
}

export function rejectFinding(
  findingId: string,
  rejectionReason: string,
  idToken: string,
  reasonCodes: readonly ReasonCode[] = [],
): Promise<FindingOut> {
  // Post-INP-343: reject takes EITHER a non-empty free-text reason
  // OR one+ reason codes. Empty/whitespace-only text is normalized
  // to undefined here so the wire-shape contract is "either codes
  // OR text, not both blank" — the server's FindingRejectIn validator
  // 422s a request with neither.
  const trimmed = rejectionReason.trim();
  return apiClient
    .POST('/api/findings/{finding_id}/reject', {
      params: { path: { finding_id: findingId } },
      body: {
        rejection_reason: trimmed.length > 0 ? trimmed : undefined,
        // Always send the array — empty is the API's default + makes the
        // wire shape predictable for the bulk-reject loop.
        reason_codes: [...reasonCodes],
      },
      headers: bearer(idToken),
    })
    .then(unwrap<FindingOut>);
}

export function needsReviewFinding(
  findingId: string,
  reviewNote: string,
  idToken: string,
): Promise<FindingOut> {
  return apiClient
    .POST('/api/findings/{finding_id}/needs-review', {
      params: { path: { finding_id: findingId } },
      body: { review_note: reviewNote },
      headers: bearer(idToken),
    })
    .then(unwrap<FindingOut>);
}

export function unreviewFinding(findingId: string, idToken: string): Promise<FindingOut> {
  return apiClient
    .POST('/api/findings/{finding_id}/unreview', {
      params: { path: { finding_id: findingId } },
      headers: bearer(idToken),
    })
    .then(unwrap<FindingOut>);
}

/** Set or clear the practitioner manual recovery-amount override.
 *  Pass a numeric string to set, `null` to clear back to the rule's
 *  computed value. Server response carries the updated finding. */
export function setRecoveryOverride(
  findingId: string,
  amount: string | null,
  idToken: string,
): Promise<FindingOut> {
  return apiClient
    .PATCH('/api/findings/{finding_id}/recovery-amount', {
      params: { path: { finding_id: findingId } },
      body: { amount },
      headers: bearer(idToken),
    })
    .then(unwrap<FindingOut>);
}

/** Fetch a finding's disposition history (oldest transition first) —
 *  powers the "approved → invoice showed no tax → rejected" trail on
 *  the finding card. Loaded lazily when the card is opened. */
export function listFindingEvents(
  findingId: string,
  idToken: string,
): Promise<FindingStatusEventListOut> {
  return apiClient
    .GET('/api/findings/{finding_id}/events', {
      params: { path: { finding_id: findingId } },
      headers: bearer(idToken),
    })
    .then(unwrap<FindingStatusEventListOut>);
}

/** The finding's recovery-amount edit history, oldest edit first (INP-522). */
export function listRecoveryEvents(
  findingId: string,
  idToken: string,
): Promise<FindingRecoveryEventListOut> {
  return apiClient
    .GET('/api/findings/{finding_id}/recovery-events', {
      params: { path: { finding_id: findingId } },
      headers: bearer(idToken),
    })
    .then(unwrap<FindingRecoveryEventListOut>);
}

/** Fetch the supplier drawer payload — classification + aggregates +
 *  every finding + journal entries (NetSuite-source only) — in one
 *  round-trip. Returns 404 when no finding on the run references the
 *  requested supplier name. */
export function getSupplierView(
  runId: string,
  supplierName: string,
  idToken: string,
  includeJournalEntries = true,
): Promise<SupplierViewOut> {
  return apiClient
    .GET('/api/runs/{run_id}/suppliers/{supplier_name}', {
      params: {
        path: { run_id: runId, supplier_name: supplierName },
        query: { include_journal_entries: includeJournalEntries },
      },
      headers: bearer(idToken),
    })
    .then(unwrap<SupplierViewOut>);
}

/** Supplier view keyed by the stable `suppliers` row id. Preferred over
 *  `getSupplierView` (name-keyed) because a supplier name can contain a
 *  `/`, which breaks a URL path segment; the id is a UUID and always
 *  round-trips safely. */
export function getSupplierViewById(
  runId: string,
  supplierId: string,
  idToken: string,
  includeJournalEntries = true,
): Promise<SupplierViewOut> {
  return apiClient
    .GET('/api/runs/{run_id}/suppliers/by-id/{supplier_id}', {
      params: {
        path: { run_id: runId, supplier_id: supplierId },
        query: { include_journal_entries: includeJournalEntries },
      },
      headers: bearer(idToken),
    })
    .then(unwrap<SupplierViewOut>);
}

/** Current BN per supplier on a run, keyed by `supplier_id`. Backs the
 *  suppliers-tab BN column — sourced from `suppliers.bn` (the curated
 *  "current BN") rather than the near-always-null finding evidence
 *  snapshot. Suppliers with no BN are absent from the map. */
export function getRunSupplierBns(
  runId: string,
  idToken: string,
): Promise<SupplierBnMapOut> {
  return apiClient
    .GET('/api/runs/{run_id}/supplier-bns', {
      params: { path: { run_id: runId } },
      headers: bearer(idToken),
    })
    .then(unwrap<SupplierBnMapOut>);
}

/** Customer<->supplier transaction network for one run — the supplier
 *  network graph (nodes = customer hub + suppliers; edges = transaction
 *  rollups). Backs the standalone `/runs/$id/network` view. */
export function getRunSupplierGraph(
  runId: string,
  idToken: string,
): Promise<SupplierGraphOut> {
  return apiClient
    .GET('/api/runs/{run_id}/supplier-graph', {
      params: { path: { run_id: runId } },
      headers: bearer(idToken),
    })
    .then(unwrap<SupplierGraphOut>);
}

/** The supplier's journal entries on one run, loaded separately from the
 *  main view so the page shell renders first (INP-517). `entries` is null
 *  for non-NetSuite runs. */
export function getSupplierJournalEntries(
  runId: string,
  supplierName: string,
  idToken: string,
): Promise<SupplierJournalEntriesOut> {
  return apiClient
    .GET('/api/runs/{run_id}/suppliers/{supplier_name}/journal-entries', {
      params: { path: { run_id: runId, supplier_name: supplierName } },
      headers: bearer(idToken),
    })
    .then(unwrap<SupplierJournalEntriesOut>);
}

/** Id-keyed counterpart to `getSupplierJournalEntries` — safe against a
 *  `/` in the supplier name (see `getSupplierViewById`). */
export function getSupplierJournalEntriesById(
  runId: string,
  supplierId: string,
  idToken: string,
): Promise<SupplierJournalEntriesOut> {
  return apiClient
    .GET('/api/runs/{run_id}/suppliers/by-id/{supplier_id}/journal-entries', {
      params: { path: { run_id: runId, supplier_id: supplierId } },
      headers: bearer(idToken),
    })
    .then(unwrap<SupplierJournalEntriesOut>);
}

/** "Explain this finding" — a CRA-cited plain-language explanation of why the
 *  rule fires and what determines recoverability. Read-only (design §7.2). */
export function explainFinding(findingId: string, idToken: string): Promise<KbAnswerOut> {
  return apiClient
    .POST('/api/findings/{finding_id}/explain', {
      params: { path: { finding_id: findingId } },
      headers: bearer(idToken),
    })
    .then(unwrap<KbAnswerOut>);
}

/** Suggest a disposition for a finding, grounded in CRA guidance (design §7.3).
 *  Advisory only — the finding's own status is unchanged; the practitioner
 *  accepts or overrides. Returns the suggested disposition + reason code +
 *  cited rationale + confidence, or an `abstained` needs-review when the
 *  evidence is thin. */
export function suggestDisposition(findingId: string, idToken: string): Promise<SuggestionOut> {
  return apiClient
    .POST('/api/findings/{finding_id}/suggest', {
      params: { path: { finding_id: findingId } },
      headers: bearer(idToken),
    })
    .then(unwrap<SuggestionOut>);
}
